
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Toaster, toast } from 'react-hot-toast';
// FIX: The modular Firebase imports are replaced with the compat import to resolve module errors.
import firebase from 'firebase/compat/app';
import { auth, db, initializeFirebase } from './services/firebase';
import type { Department, StaffMember, Shift, Schedule, View, LoginCredentials, AdminUser, Notification, AssignmentStatus, ScheduleEntry, TimeOffRequest, TimeOffRequestStatus, Theme, AvailabilityStatus, DayOfWeek, PickupLocation, ReservationSource, ShuttleReservation, ParkingReservation, AppPage, WeeklyMetrics } from './types';
import { ALL_DAYS, ALL_PAGES, DAY_INDEX_MAP } from './types';
import { subscribeToNotifications, markNotificationAsRead, markAllNotificationsAsRead, createNotification, clearNotifications } from './services/notificationService';
import { formatDateRange, getWeekStartDate, getWeekDates, formatDateYYYYMMDD, getDayOfWeekFromDate } from './utils/dateUtils';
import { Icons } from './components/ui/Icons';

import Login from './components/auth/Login';
import Header from './components/layout/Header';
import SchedulerView from './components/scheduler/SchedulerView';
import StaffManagement from './components/staff/StaffManagement';
import SettingsView from './components/settings/SettingsView';
import StaffPortal from './components/staff_portal/StaffPortal';
import AdminProfileView from './components/admin/AdminProfileView';
import LoadingSpinner from './components/layout/LoadingSpinner';
import PermissionErrorMessage from './components/auth/PermissionErrorMessage';
import TimeOffManagementView from './components/admin/TimeOffManagementView';
import PrintPage from './components/scheduler/PrintPage';
import type { PrintData } from './components/scheduler/PrintPage';
import PDFPreviewPage from './components/scheduler/PDFPreviewPage';
import type { PDFPreviewData } from './components/scheduler/PDFPreviewPage';
import ShuttlePrintPage from './components/shuttle/ShuttlePrintPage';
import type { ShuttlePrintData } from './components/shuttle/ShuttlePrintPage';
import ParkingPrintPage from './components/parking/ParkingPrintPage';
import type { ParkingPrintData } from './components/parking/ParkingPrintPage';
import WorkforceTracker from './components/search/WorkforceTracker';
import ShuttleReservationView from './components/shuttle/ShuttleReservationView';
import ParkingReservationView from './components/parking/ParkingReservationView';
import HelpCenterView from './components/help/HelpCenterView';
import ReportsView from './components/reports/ReportsView';

const App: React.FC = () => {
    // FIX: Use `firebase.auth.User` type from the compat library.
    // Fix: Corrected firebase.auth.User to firebase.User as per compat library usage.
    const [user, setUser] = useState<firebase.User | null>(null);
    const [loggedInStaff, setLoggedInStaff] = useState<StaffMember | null>(null);
    const [isFirebaseInitialized, setIsFirebaseInitialized] = useState(false);
    const [firebaseInitError, setFirebaseInitError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [firebasePermissionError, setFirebasePermissionError] = useState(false);
    
    const [theme, setTheme] = useState<Theme>(() => (localStorage.getItem('theme') as Theme) || 'system');
    const [isDarkMode, setIsDarkMode] = useState(false);

    const [departments, setDepartments] = useState<Department[]>([]);
    const [staffList, setStaffList] = useState<StaffMember[]>([]);
    const [shifts, setShifts] = useState<Shift[]>([]);
    const [pickupLocations, setPickupLocations] = useState<PickupLocation[]>([]);
    const [reservationSources, setReservationSources] = useState<ReservationSource[]>([]);
    const [shuttleReservations, setShuttleReservations] = useState<ShuttleReservation[]>([]);
    const [parkingReservations, setParkingReservations] = useState<ParkingReservation[]>([]);
    const [weekStartDay, setWeekStartDay] = useState<DayOfWeek>('Monday');
    const [schedule, setSchedule] = useState<Schedule>({});
    const [weeklyMetrics, setWeeklyMetrics] = useState<WeeklyMetrics>({});
    const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const notificationsRef = useRef<Notification[]>([]);
    const processedReservationsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        notificationsRef.current = notifications;
    }, [notifications]);
    const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequest[]>([]);
    const [printData, setPrintData] = useState<PrintData | null>(null);
    const [pdfPreviewData, setPdfPreviewData] = useState<PDFPreviewData | null>(null);
    const [shuttlePrintData, setShuttlePrintData] = useState<ShuttlePrintData | null>(null);
    const [parkingPrintData, setParkingPrintData] = useState<ParkingPrintData | null>(null);
    const [propertyName, setPropertyName] = useState<string>('Quality INN & SUITES');

    const [view, setView] = useState<View>({ page: 'scheduler' });
    const [selectedDate, setSelectedDate] = useState<string>(() => {
        const now = new Date();
        const hour = now.getHours();
        if (hour >= 14) {
            const tomorrow = new Date(now);
            tomorrow.setDate(now.getDate() + 1);
            return formatDateYYYYMMDD(tomorrow);
        }
        return formatDateYYYYMMDD(now);
    });

    const currentAdminUser = useMemo(() => {
        if (!user) return null;
        return adminUsers.find(a => a.uid === user.uid) || null;
    }, [user, adminUsers]);

    const allowedPages = useMemo(() => {
        // If not logged in as an admin in the database, return all pages
        if (!currentAdminUser) return ALL_PAGES;
        
        // Use their defined pages or default to all pages if not set
        const pages = currentAdminUser.allowedPages || ALL_PAGES;
        
        // Ensure myProfile and help are always available to prevent lockouts
        const essentialPages: AppPage[] = ['myProfile', 'help'];
        const combined = [...new Set([...pages, ...essentialPages])];
        
        return combined as AppPage[];
    }, [currentAdminUser]);

    const handleSetView = useCallback((newView: View) => {
        if (user && !allowedPages.includes(newView.page)) {
            toast.error(`You do not have permission to access the ${newView.page} page.`);
            return;
        }
        setView(newView);
    }, [user, allowedPages]);

    const defaultAvailability = useMemo(() => ALL_DAYS.reduce((acc, day) => {
        acc[day] = 'Available';
        return acc;
    }, {} as { [key in DayOfWeek]: AvailabilityStatus }), []);

     useEffect(() => {
        if ('serviceWorker' in navigator) {
          window.addEventListener('load', () => {
            navigator.serviceWorker.register('/service-worker.js')
              .then(registration => {
                console.log('ServiceWorker registration successful with scope: ', registration.scope);
                
                // Check for updates when the app is launched from home screen
                registration.update();

                // Listen for new service worker installation
                registration.addEventListener('updatefound', () => {
                  const newWorker = registration.installing;
                  if (newWorker) {
                    newWorker.addEventListener('statechange', () => {
                      if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                        // A new service worker is available and installed
                        console.log('New version available! Refreshing...');
                        // Prevent infinite reload loops
                        const lastReload = sessionStorage.getItem('sw-reload');
                        if (lastReload && Date.now() - parseInt(lastReload) < 10000) {
                          console.log('Prevented reload loop');
                          return;
                        }
                        sessionStorage.setItem('sw-reload', Date.now().toString());
                        window.location.reload();
                      }
                    });
                  }
                });
              }).catch(error => {
                console.log('ServiceWorker registration failed: ', error);
              });

            // Handle the case where the service worker takes control
            let refreshing = false;
            navigator.serviceWorker.addEventListener('controllerchange', () => {
              if (!refreshing) {
                const lastReload = sessionStorage.getItem('sw-reload');
                if (lastReload && Date.now() - parseInt(lastReload) < 10000) {
                  console.log('Prevented reload loop');
                  return;
                }
                sessionStorage.setItem('sw-reload', Date.now().toString());
                refreshing = true;
                window.location.reload();
              }
            });
          });
        }
      }, []);

      useEffect(() => {
        const init = async () => {
            try {
                await initializeFirebase();
                setIsFirebaseInitialized(true);
            } catch (error: any) {
                console.error("Firebase initialization failed:", error);
                setFirebaseInitError(error.message || "Failed to initialize system.");
                toast.error("Failed to initialize system. Please check your configuration.");
                setIsLoading(false);
            }
        };
        init();
      }, []);

     useEffect(() => {
        const root = window.document.documentElement;
        const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
        
        setIsDarkMode(isDark);
        root.classList.remove(isDark ? 'light' : 'dark');
        root.classList.add(isDark ? 'dark' : 'light');
        
        localStorage.setItem('theme', theme);

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = () => {
             if (theme === 'system') {
                const newIsDark = mediaQuery.matches;
                setIsDarkMode(newIsDark);
                root.classList.remove(newIsDark ? 'light' : 'dark');
                root.classList.add(newIsDark ? 'dark' : 'light');
            }
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [theme]);


    const saveData = useCallback(async (data: { departments: Department[], shifts: Shift[], schedule: Schedule, weeklyMetrics: WeeklyMetrics, pickupLocations: PickupLocation[], reservationSources: ReservationSource[], shuttleReservations: ShuttleReservation[], parkingReservations: ParkingReservation[], weekStartDay: DayOfWeek, propertyName: string }) => {
        if (!user || !isFirebaseInitialized) return;
        setIsSaving(true);
        try {
            await db.ref('scheduleData').update({
                departments: data.departments,
                shifts: data.shifts,
                pickupLocations: data.pickupLocations,
                reservationSources: data.reservationSources,
                shuttleReservations: data.shuttleReservations,
                parkingReservations: data.parkingReservations,
                schedule: data.schedule,
                weeklyMetrics: data.weeklyMetrics,
                weekStartDay: data.weekStartDay,
                propertyName: data.propertyName
            });
            
            setFirebasePermissionError(false);
        } catch (error: any) {
            console.error("Firebase write error:", error);
            if (error.code === 'PERMISSION_DENIED' || error.message?.toLowerCase().includes('permission denied')) {
                // Only set fatal error if we are logged in but can't save core data
                if (user) setFirebasePermissionError(true);
                toast.error('Permission denied: You do not have permission to save changes.');
            } else {
                toast.error('Failed to save data. Check your connection.');
            }
        } finally {
            setTimeout(() => setIsSaving(false), 500);
        }
    }, [user]);

    useEffect(() => {
        // Don't save on initial data load or if not logged in.
        if (isLoading || !user || !isFirebaseInitialized) return;
    
        saveData({ departments, shifts, schedule, weeklyMetrics, pickupLocations, reservationSources, shuttleReservations, parkingReservations, weekStartDay, propertyName });
        
        // This effect now saves data immediately upon changes to departments, shifts, schedule, weeklyMetrics, pickupLocations, reservationSources, shuttleReservations, parkingReservations, or weekStartDay.
    }, [departments, shifts, schedule, weeklyMetrics, pickupLocations, reservationSources, shuttleReservations, parkingReservations, weekStartDay, propertyName, saveData, isLoading, user, isFirebaseInitialized]);
    
    useEffect(() => {
        if (!isFirebaseInitialized) return;

        // Listener for core schedule data
        const dataRef = db.ref('scheduleData');
        const dataCallback = (snapshot: firebase.database.DataSnapshot) => {
            const data = snapshot.val();
            const toDenseArray = (value: any[] | { [key: string]: any } | null | undefined): any[] => {
                if (!value) return [];
                if (Array.isArray(value)) return value.filter(Boolean);
                if (typeof value === 'object') return Object.values(value).filter(Boolean);
                return [];
            };

            if (data) {
                setDepartments(data.departments ? toDenseArray(data.departments) : []);
                setStaffList(data.staffList ? toDenseArray(data.staffList) : []);
                setShifts(data.shifts ? toDenseArray(data.shifts) : []);
                setPickupLocations(data.pickupLocations ? toDenseArray(data.pickupLocations) : []);
                setReservationSources(data.reservationSources ? toDenseArray(data.reservationSources) : []);
                setShuttleReservations(data.shuttleReservations ? toDenseArray(data.shuttleReservations) : []);
                setParkingReservations(data.parkingReservations ? toDenseArray(data.parkingReservations) : []);
                setWeekStartDay(data.weekStartDay || 'Monday');
                setPropertyName(data.propertyName || 'Quality INN & SUITES');
                setSchedule(data.schedule || {});
                setWeeklyMetrics(data.weeklyMetrics || {});
            } else {
                // If there's no data in Firebase, initialize with empty state.
                // The app will now start empty if the database is empty.
                setDepartments([]);
                setStaffList([]);
                setShifts([]);
                setPickupLocations([]);
                setShuttleReservations([]);
                setParkingReservations([]);
                setWeekStartDay('Monday');
                setPropertyName('Quality INN & SUITES');
                setSchedule({});
                setWeeklyMetrics({});
            }
            setIsLoading(false);
        };
        const dataErrorCallback = (error: Error) => {
            console.error("Firebase read error:", error);
            // Only show fatal error screen if we are logged in as admin but still can't read core data
            if (user && (error.message.toLowerCase().includes('permission_denied') || error.message.toLowerCase().includes('permission denied'))) {
                setFirebasePermissionError(true);
            }
            toast.error('Could not fetch schedule data.');
            setIsLoading(false);
        };
        dataRef.on('value', dataCallback, dataErrorCallback);

        // Separate listener for time-off requests
        const timeOffRef = db.ref('timeOffRequests');
        const timeOffCallback = (snapshot: firebase.database.DataSnapshot) => {
            const requestsData = snapshot.val();
            if (requestsData && typeof requestsData === 'object' && requestsData !== null) {
                const requestsArray = Object.keys(requestsData).map(key => ({ id: key, ...requestsData[key] }));
                setTimeOffRequests(requestsArray);
            } else {
                setTimeOffRequests([]);
            }
        };
        const timeOffErrorCallback = (error: Error) => {
             console.error("Firebase time off read error:", error);
             // Non-fatal: just toast and log
             if (!error.message.toLowerCase().includes('permission_denied') && !error.message.toLowerCase().includes('permission denied')) {
                toast.error('Could not fetch time off requests.');
             }
        };
        timeOffRef.on('value', timeOffCallback, timeOffErrorCallback);

        // Admins listener - Always listen to detect if we need to register the first admin
        const adminsRef = db.ref('admins');
        const adminsCallback = (snapshot: firebase.database.DataSnapshot) => {
            const data = snapshot.val();
            const loadedAdmins = data ? Object.keys(data).map(key => ({ uid: key, ...data[key] })) : [];
            setAdminUsers(loadedAdmins);
            setFirebasePermissionError(false);
        };
        const adminsErrorCallback = (error: Error) => {
            // Firebase logs permission denied to console automatically.
            // We do NOT trigger the fatal error UI for admin list read failures, 
            // as regular admins or staff might not have permission to see the whole list.
            if (error.message.toLowerCase().includes('permission_denied') || error.message.toLowerCase().includes('permission denied')) {
                if (!user) {
                    // Silently assume admins exist if we can't read the list while logged out
                    if (adminUsers.length === 0) {
                        setAdminUsers([{ uid: 'exists', name: 'Existing Admin', email: '', role: 'Admin', status: 'active' }]);
                    }
                }
            } else {
                console.error("Firebase admins read error:", error);
                toast.error('Could not fetch administrator data.');
            }
        };
        
        adminsRef.on('value', adminsCallback, adminsErrorCallback);

        const authUnsubscribe = auth.onAuthStateChanged((currentUser) => {
            if (currentUser && (currentUser.isAnonymous || currentUser.email === 'staff@qualityinn.com')) {
                // Ignore anonymous users and generic staff account for admin purposes
                return;
            }
            setUser(currentUser);
            if (!currentUser) {
                setView({ page: 'scheduler' });
            }
        });

        return () => {
            dataRef.off('value', dataCallback);
            timeOffRef.off('value', timeOffCallback);
            adminsRef.off('value', adminsCallback);
            authUnsubscribe();
        };
    }, [user, saveData, isFirebaseInitialized]);
    
    useEffect(() => {
        const currentUserId = user?.uid || loggedInStaff?.id;
        if (!currentUserId || !isFirebaseInitialized) {
            setNotifications([]);
            return;
        }

        const unsubscribe = subscribeToNotifications(currentUserId, (newNotifications) => {
            setNotifications(newNotifications);
        });

        return () => unsubscribe();
    }, [user, loggedInStaff]);

    useEffect(() => {
        const savedStaffId = localStorage.getItem('scheduler_staff_id');
        
        if (staffList.length > 0) {
            if (loggedInStaff) {
                // Check if currently logged in staff was updated or deleted
                const updatedStaff = staffList.find(s => s.id === loggedInStaff.id);
                if (!updatedStaff || updatedStaff.status === 'deleted') {
                    setLoggedInStaff(null);
                    localStorage.removeItem('scheduler_staff_id');
                    toast.error('Your account has been removed or deactivated.');
                } else if (JSON.stringify(updatedStaff) !== JSON.stringify(loggedInStaff)) {
                    setLoggedInStaff(updatedStaff);
                }
            } else if (savedStaffId) {
                // Auto-login from localStorage
                const staff = staffList.find(s => s.id === savedStaffId && s.status !== 'deleted');
                if (staff) {
                    setLoggedInStaff(staff);
                    // Ensure they are authenticated with Firebase to bypass permission denied errors
                    if (!auth.currentUser || (auth.currentUser.email !== 'staff@qualityinn.com' && !auth.currentUser.isAnonymous)) {
                        auth.signInWithEmailAndPassword('staff@qualityinn.com', 'StaffPassword123!').catch(authError => {
                            if (authError.code === 'auth/user-not-found' || authError.code === 'auth/invalid-credential') {
                                auth.createUserWithEmailAndPassword('staff@qualityinn.com', 'StaffPassword123!').catch(e => console.warn(e));
                            }
                        });
                    }
                } else {
                    localStorage.removeItem('scheduler_staff_id');
                }
            }
        }
    }, [staffList, loggedInStaff]);
    
    // Check for late parking reservations and notify admins
    useEffect(() => {
        if (!user || parkingReservations.length === 0) return;

        const checkLateReservations = async () => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            for (const res of parkingReservations) {
                if (res.status === 'Checkout') continue;
                
                const endDate = new Date(res.endDate);
                endDate.setHours(0, 0, 0, 0);

                if (today.getTime() > endDate.getTime()) {
                    // Check if we already processed this in the current session
                    if (processedReservationsRef.current.has(res.confirmationNumber)) continue;

                    // It's late. Check if we already notified for this specific reservation in the persistent list.
                    const alreadyNotified = notificationsRef.current.some(n => 
                        n.message.includes(res.confirmationNumber) && 
                        (n.message.includes('passed') || n.title.includes('Late'))
                    );

                    if (!alreadyNotified) {
                        // Mark as processed immediately to avoid double-firing
                        processedReservationsRef.current.add(res.confirmationNumber);
                        
                        await createNotification(user.uid, {
                            title: 'Late Parking Reservation',
                            message: `Parking reservation #${res.confirmationNumber} for ${res.guestName} has passed its end date (${res.endDate}).`,
                            level: 'warning'
                        });
                    }
                }
            }
        };

        // Only run this check if the user is an admin
        const isAdminUser = adminUsers.some(a => a.uid === user.uid);
        if (isAdminUser) {
            checkLateReservations();
        }
    }, [parkingReservations, user, adminUsers]);

    const handleRegisterFirstAdmin = async ({ name, email, password }: LoginCredentials) => {
        if (adminUsers.length > 0 || !isFirebaseInitialized) {
            if (adminUsers.length > 0) toast.error("An administrator already exists. Please log in.");
            return;
        }
        setIsLoading(true);
        try {
            const userCredential = await auth.createUserWithEmailAndPassword(email, password);
            if (userCredential.user) {
                await db.ref(`admins/${userCredential.user.uid}`).set({
                    name,
                    email,
                    role: 'Super Admin',
                    status: 'active',
                    createdAt: new Date().toISOString(),
                    allowedPages: ALL_PAGES
                });
                toast.success("First administrator registered successfully!");
            }
        } catch (error: any) {
            console.error("Registration Error:", error);
            toast.error(error.message || "Failed to register administrator.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogin = async ({ email, password }: Omit<LoginCredentials, 'name'>, rememberMe: boolean) => {
        if (!isFirebaseInitialized) return;
        setIsLoading(true);
        try {
            await auth.setPersistence(rememberMe ? firebase.auth.Auth.Persistence.LOCAL : firebase.auth.Auth.Persistence.SESSION);
            
            // Attempt Admin Login via Firebase Auth
            await auth.signInWithEmailAndPassword(email, password);
            
            // If we get here, Firebase Auth was successful.
            // Now, verify the user is a valid admin in our database.
            const adminUser = auth.currentUser;
            if (!adminUser) {
                await auth.signOut();
                throw new Error("Login failed: User object not found after authentication.");
            }
            
            // Add a small delay to ensure auth state propagates to the database rules engine
            await new Promise(resolve => setTimeout(resolve, 200));
            
            let adminSnapshot;
            try {
                adminSnapshot = await db.ref(`admins/${adminUser.uid}`).get();
            } catch (dbError: any) {
                // Retry once if it's a permission error, as auth state might still be propagating
                if (dbError.message?.toLowerCase().includes('permission denied') || dbError.code === 'PERMISSION_DENIED') {
                    await new Promise(resolve => setTimeout(resolve, 800));
                    adminSnapshot = await db.ref(`admins/${adminUser.uid}`).get();
                } else {
                    throw dbError;
                }
            }
            
            setFirebasePermissionError(false);

            if (adminSnapshot.exists()) {
                if (adminSnapshot.val().status === 'suspended') {
                    await auth.signOut();
                    toast.error('Your admin account has been suspended.');
                } else {
                    toast.success('Admin login successful!');
                }
            } else {
                // User authenticated with Firebase, but has no admin entry in our DB.
                // This is a permission error, not a credential error.
                await auth.signOut();
                toast.error('This account is not registered as an administrator.');
            }

        } catch (error: any) {
            // This catch block handles ALL failures from the `try` block above, including auth failures.
            
            // We only fall back to staff login if it was a credential-related auth error.
            if (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
                
                // Try to find a matching staff member
                const staffMember = staffList.find(s => s.email.toLowerCase() === email.toLowerCase() && s.password === password && s.status !== 'deleted');
                
                if (staffMember) {
                    // Staff login successful
                    try {
                        await auth.signInWithEmailAndPassword('staff@qualityinn.com', 'StaffPassword123!');
                    } catch (authError: any) {
                        if (authError.code === 'auth/user-not-found' || authError.code === 'auth/invalid-credential') {
                            try {
                                await auth.createUserWithEmailAndPassword('staff@qualityinn.com', 'StaffPassword123!');
                            } catch (createError) {
                                console.warn("Failed to create generic staff account:", createError);
                            }
                        } else {
                            console.warn("Failed to sign in with generic staff account:", authError);
                        }
                    }
                    setLoggedInStaff(staffMember);
                    if (rememberMe) {
                        localStorage.setItem('scheduler_staff_id', staffMember.id);
                    } else {
                        localStorage.removeItem('scheduler_staff_id');
                    }
                    toast.success(`Welcome, ${staffMember.name}!`);
                } else {
                    // Admin login failed AND staff login failed.
                    toast.error('Invalid credentials. Please check your email and password.');
                }
            } else {
                // A non-credential error occurred (e.g., network issue or permission denied)
                console.error("Login Error:", error);
                
                // If we get a permission denied here, it means the user is authenticated
                // but the database rules are blocking them from reading their own admin profile.
                if (error.message?.toLowerCase().includes('permission denied') || error.code === 'PERMISSION_DENIED') {
                    // This is a serious configuration issue, so we show the error screen
                    setFirebasePermissionError(true);
                    toast.error('Permission denied. Your account may not have the required permissions.');
                } else {
                    toast.error(error.message || 'An unexpected login error occurred.');
                }
            }
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleForgotPassword = async (email: string) => {
        if (!isFirebaseInitialized) return;
        if (!email) {
            toast.error('Please enter your email address in the email field first.');
            return;
        }
        setIsLoading(true);
        try {
            // FIX: Use the compat `auth.sendPasswordResetEmail()` method.
            await auth.sendPasswordResetEmail(email);
            toast.success(`Password reset email sent to ${email}. Please check your inbox.`);
        } catch (error: any) {
            console.error("Forgot Password Error:", error);
            toast.error(error.message || 'Failed to send password reset email.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = async () => {
        if (!isFirebaseInitialized) return;
        // FIX: Use the compat `auth.signOut()` method.
        if (user) await auth.signOut();
        if (loggedInStaff) {
            setLoggedInStaff(null);
            localStorage.removeItem('scheduler_staff_id');
        }
        toast.success('Logged out successfully.');
    };

    const handleStaffUpdateBySelf = useCallback(async (staff: StaffMember) => {
        if (!isFirebaseInitialized) return;
        const staffIndex = staffList.findIndex(s => s.id === staff.id);
        if (staffIndex === -1) {
            toast.error("Could not find your profile to update.");
            return;
        }

        try {
            await db.ref(`scheduleData/staffList/${staffIndex}`).set(staff);
            setLoggedInStaff(staff); // Update logged-in staff state
            setStaffList(prev => prev.map(s => s.id === staff.id ? staff : s));
            toast.success(`Your profile has been updated, ${staff.name.split(' ')[0]}.`);
    
            const title = "Profile Updated by Staff";
            const message = `${staff.name} just updated their profile. Review the changes to stay up-to-date.`;
            const notificationPromises = adminUsers.map(admin =>
                createNotification(admin.uid, { title, message, level: 'info' })
            );
            await Promise.all(notificationPromises);
        } catch (error: any) {
            console.error("Staff profile save error:", error);
            if (error.code === 'PERMISSION_DENIED' || error.message?.toLowerCase().includes('permission denied')) {
                toast.error('Permission denied: You do not have access to update this profile.');
            } else {
                toast.error('Failed to save your profile.');
            }
        }
    }, [adminUsers, staffList]);
    
    const handleStaffProfileUpdateByAdmin = useCallback(async (staff: StaffMember) => {
        if (!isFirebaseInitialized) return;
        const isNewStaff = !staff.id;
        const staffToSave = { ...staff, id: isNewStaff ? `staff${Date.now()}` : staff.id, status: staff.status || 'active' };

        const staffIndex = isNewStaff
            ? staffList.length
            : staffList.findIndex(s => s.id === staffToSave.id);

        if (staffIndex === -1 && !isNewStaff) {
            toast.error("Could not find staff member to update.");
            return;
        }

        try {
            await db.ref(`scheduleData/staffList/${staffIndex}`).set(staffToSave);

            setStaffList(prev => {
                const newList = [...prev];
                newList[staffIndex] = staffToSave;
                return newList;
            });
            if (isNewStaff) {
                setSchedule(prev => ({ ...prev, [staffToSave.id]: {} }));
            }
            toast.success(`Staff member ${staffToSave.name} saved.`);

            if (!isNewStaff) {
                const title = "Your Profile Was Updated";
                const message = "An administrator has updated your profile. Please log in to your portal to review the changes.";
                createNotification(staff.id, { title, message, level: 'info' })
                    .catch(err => console.error(`Failed to notify staff ${staff.id} of profile update:`, err));
            }
        } catch (error: any) {
            console.error("Admin save staff error:", error);
            if (error.code === 'PERMISSION_DENIED' || error.message?.toLowerCase().includes('permission denied')) {
                toast.error("Permission denied: You do not have permission to manage staff.");
            } else {
                toast.error("Failed to save staff data.");
            }
        }
    }, [staffList]);


    const handleDeleteStaff = useCallback(async (staffId: string) => {
        if (!isFirebaseInitialized) return;
        const staffIndex = staffList.findIndex(s => s.id === staffId);
        const staffToDelete = staffList[staffIndex];
        
        if (staffIndex > -1 && staffToDelete) {
            try {
                // Soft delete by setting status
                await db.ref(`scheduleData/staffList/${staffIndex}/status`).set('deleted');
                // Update local state
                setStaffList(prev => prev.map(s => s.id === staffId ? { ...s, status: 'deleted' } : s));
            } catch (error: any) {
                console.error("Delete staff error:", error);
                if (error.code === 'PERMISSION_DENIED' || error.message?.toLowerCase().includes('permission denied')) {
                    toast.error("Permission denied: You do not have permission to delete staff.");
                } else {
                    toast.error("Failed to delete staff member.");
                }
                // Re-throw the error to be caught by the central handler
                throw new Error("Failed to delete staff member.");
            }
        }
    }, [staffList]);
    
    const handleSaveDepartment = useCallback((department: Omit<Department, 'id'> & { id?: string }) => {
        const newDepartment = department.id ? { ...department, id: department.id } : { ...department, id: `dep${Date.now()}` };
        setDepartments(prev => department.id ? prev.map(d => d.id === newDepartment.id ? newDepartment : d) : [...prev, newDepartment]);
        toast.success(`Department "${newDepartment.name}" ${department.id ? 'updated' : 'added'}.`);
    }, []);

    const handleDeleteDepartment = useCallback((departmentId: string) => {
        const departmentToDelete = departments.find(d => d.id === departmentId);
        if (departmentToDelete) {
            setDepartments(prev => prev.filter(d => d.id !== departmentId));
            setStaffList(prev => prev.map(staff => staff.departmentId === departmentId ? { ...staff, departmentId: null } : staff));
        }
    }, [departments]);
    
    const handleSaveShift = useCallback((shift: Omit<Shift, 'id'> & { id?: string }) => {
        const newShift = shift.id ? { ...shift, id: shift.id } : { ...shift, id: `shift${Date.now()}` };
// FIX: The `map` function parameter `s` was correct, but the else branch was returning an undefined variable `d`. Corrected `d` to `s`.
        setShifts(prev => shift.id ? prev.map(s => s.id === newShift.id ? newShift : s) : [...prev, newShift]);
        toast.success(`Shift "${newShift.name}" ${shift.id ? 'updated' : 'added'}.`);
    }, []);
    
    const handleSavePickupLocation = useCallback((location: Omit<PickupLocation, 'id'> & { id?: string }) => {
        const newLocation = location.id ? { ...location, id: location.id } : { ...location, id: `loc${Date.now()}` };
        setPickupLocations(prev => location.id ? prev.map(l => l.id === newLocation.id ? newLocation : l) : [...prev, newLocation]);
        toast.success(`Location "${newLocation.name}" ${location.id ? 'updated' : 'added'}.`);
    }, []);

    const handleDeletePickupLocation = useCallback((locationId: string) => {
        const locationToDelete = pickupLocations.find(l => l.id === locationId);
        if (locationToDelete) {
            setPickupLocations(prev => prev.filter(l => l.id !== locationId));
        }
    }, [pickupLocations]);

    const handleSaveReservationSource = useCallback((source: Omit<ReservationSource, 'id'> & { id?: string }) => {
        const newSource = source.id ? { ...source, id: source.id } : { ...source, id: `src${Date.now()}` };
        setReservationSources(prev => source.id ? prev.map(s => s.id === newSource.id ? newSource : s) : [...prev, newSource]);
        toast.success(`Source "${newSource.name}" ${source.id ? 'updated' : 'added'}.`);
    }, []);

    const handleDeleteReservationSource = useCallback((sourceId: string) => {
        const sourceToDelete = reservationSources.find(s => s.id === sourceId);
        if (sourceToDelete) {
            setReservationSources(prev => prev.filter(s => s.id !== sourceId));
        }
    }, [reservationSources]);

    const handleSaveShuttleReservation = useCallback((reservation: Omit<ShuttleReservation, 'id'> & { id?: string }) => {
        if (reservation.id) {
            setShuttleReservations(prev => prev.map(r => r.id === reservation.id ? { ...r, ...reservation } as ShuttleReservation : r));
            toast.success(`Reservation for Room ${reservation.roomNumber} updated.`);
        } else {
            const newReservation = { ...reservation, id: `res${Date.now()}` } as ShuttleReservation;
            setShuttleReservations(prev => [...prev, newReservation]);
            toast.success(`Reservation for Room ${reservation.roomNumber} confirmed.`);
        }
    }, []);

    const handleDeleteShuttleReservation = useCallback((reservationId: string) => {
        setShuttleReservations(prev => prev.filter(r => r.id !== reservationId));
        toast.success('Reservation deleted.');
    }, []);

    const handleDeleteShift = useCallback((shiftId: string) => {
        const shiftToDelete = shifts.find(s => s.id === shiftId);
        if (shiftToDelete) {
            setShifts(prev => prev.filter(s => s.id !== shiftId));
            setSchedule(prev => {
                const newSchedule = JSON.parse(JSON.stringify(prev));
                Object.values(newSchedule).forEach((staffSchedule: any) => {
                    Object.keys(staffSchedule).forEach(date => {
                        if (staffSchedule[date].shiftId === shiftId) {
                            staffSchedule[date] = { ...staffSchedule[date], shiftId: null, status: 'UNASSIGNED' };
                        }
                    });
                });
                return newSchedule;
            });
        }
    }, [shifts]);

    const handleCreateAdmin = async (credentials: LoginCredentials) => {
        if (!isFirebaseInitialized) return;
        try {
            // FIX: Use the compat `auth.createUserWithEmailAndPassword()` method.
            const userCredential = await auth.createUserWithEmailAndPassword(credentials.email, credentials.password);
            const newUser = userCredential.user;
            if (!newUser) throw new Error("Admin creation failed.");
            const newAdmin: Omit<AdminUser, 'uid' | 'email' | 'status'> = {
                name: credentials.name || 'New Admin',
                color: '#8b5cf6', phone: '', role: 'Administrator', notes: '', departmentId: null,
                availability: defaultAvailability,
                availabilityOverrides: {},
                showInScheduler: false,
                allowedPages: ALL_PAGES,
            };
            // FIX: Use the compat `db.ref().set()` method.
            await db.ref(`admins/${newUser.uid}`).set({
                email: newUser.email, status: 'active', ...newAdmin
            });
            toast.success(`Admin user ${credentials.email} created successfully.`);
        } catch (error: any) {
            toast.error(error.message || 'Failed to create admin.');
            throw error;
        }
    };

    const handleSaveAdmin = async (adminData: AdminUser) => {
        if (!isFirebaseInitialized) return;
        const { uid, ...dataToSave } = adminData;
        // FIX: Use the compat `db.ref().set()` method.
        await db.ref(`admins/${uid}`).set(dataToSave);
        toast.success(`Admin profile for ${adminData.name} updated.`);
    };
    
    const handleDeleteCurrentUser = async () => {
        if (!isFirebaseInitialized) return;
        const currentUser = auth.currentUser;
        if (currentUser) {
            try {
                await db.ref(`admins/${currentUser.uid}`).set(null);
                await currentUser.delete();
            } catch (error: any) {
                console.error('Failed to delete current user account:', error);
                // Re-throw to be handled by the central handler
                throw new Error(error.message || 'Failed to delete your account.');
            }
        } else {
            throw new Error("No user is currently signed in.");
        }
    };

    const handleUpdateAdminStatus = async (uid: string, status: 'active' | 'suspended') => {
        if (!isFirebaseInitialized) return;
        // FIX: Use the compat `db.ref().set()` method.
        await db.ref(`admins/${uid}/status`).set(status);
        toast.success(`Admin user status updated to ${status}.`);
    };

    const handleDeleteAdmin = async (uid: string, email: string) => {
        if (!isFirebaseInitialized) return;
        try {
            await db.ref(`admins/${uid}`).set(null);
        } catch (error: any) {
            console.error(`Failed to revoke admin permissions for ${email}:`, error);
            if (error.code === 'PERMISSION_DENIED' || error.message?.toLowerCase().includes('permission denied')) {
                toast.error("Permission denied: You do not have permission to manage other administrators.");
            } else {
                toast.error('Failed to revoke admin permissions.');
            }
             // Re-throw to be handled by the central handler
            throw new Error('Failed to revoke admin permissions.');
        }
    };
    
    const schedulablePeople = useMemo(() => {
        const activeStaff = staffList.filter(s => s.status !== 'deleted' && s.status !== 'inactive');
        const schedulableAdmins = adminUsers
            .filter(admin => admin.showInScheduler)
            .map(admin => ({ ...admin, id: admin.uid }));
        return [...activeStaff, ...schedulableAdmins];
    }, [staffList, adminUsers]);

    const handleScheduleUpdateByAdmin = useCallback((staffId: string, date: string, shiftId: string | null, status: AssignmentStatus, isForced?: boolean) => {
        if (!isFirebaseInitialized) return;
        setSchedule(prev => {
            const staffSchedule = prev[staffId] || {};
            return {
                ...prev,
                [staffId]: {
                    ...staffSchedule,
                    [date]: { staffId, shiftId, status, isForced: isForced || false },
                },
            };
        });
    
        // Notify staff member (but not admins) of manual schedule changes.
        const person = schedulablePeople.find(p => p.id === staffId);
        const isStaff = staffList.some(s => s.id === staffId);
        
        if (person && isStaff) {
            let title = "Schedule Update";
            let message = `Your schedule for ${date} has been changed by an administrator.`;

            if (status === 'ASSIGNED' && shiftId) {
                const assignedShift = shifts.find(s => s.id === shiftId);
                if (assignedShift) {
                    title = "New Shift Assigned!";
                    message = `You've been assigned to the "${assignedShift.name}" shift (${assignedShift.startTime} - ${assignedShift.endTime}) on ${date}.`;
                }
            } else {
                const statusLabel = status.replace('_', ' ').toLowerCase();
                message = `Your status for ${date} has been updated to "${statusLabel}".`;
            }

            createNotification(staffId, { title, message, level: 'info' })
                .catch(err => console.error(`Failed to notify staff ${staffId} of schedule update:`, err));
        }
    }, [schedulablePeople, staffList, shifts]);

    const handleRepeatSchedule = useCallback((sourceWeekStartDate: Date, repeatStartDate: Date, repeatEndDate: Date) => {
        const sourceWeekDates = getWeekDates(sourceWeekStartDate);
        const sourceScheduleByDay: { [dayIndex: number]: { [staffId: string]: ScheduleEntry } } = {};

        for (let i = 0; i < 7; i++) {
            const date = sourceWeekDates[i];
            const dateString = formatDateYYYYMMDD(date);
            sourceScheduleByDay[i] = {}; // 0 = Monday, 1 = Tuesday...
            for (const person of schedulablePeople) {
                const entry = schedule[person.id]?.[dateString];
                if (entry) {
                    sourceScheduleByDay[i][person.id] = entry;
                }
            }
        }

        const startDayIndex = DAY_INDEX_MAP[weekStartDay];
        let currentWeekStart = getWeekStartDate(repeatStartDate, startDayIndex);
        const end = new Date(repeatEndDate);

        setSchedule(prevSchedule => {
            const newSchedule = JSON.parse(JSON.stringify(prevSchedule));

            while (currentWeekStart <= end) {
                const targetWeekDates = getWeekDates(currentWeekStart);

                for (let i = 0; i < 7; i++) {
                    const targetDate = targetWeekDates[i];
                    if (targetDate > end) break; 

                    const targetDateString = formatDateYYYYMMDD(targetDate);
                    // FIX: Corrected variable from `sourceDaySchedule` to `sourceScheduleByDay` to prevent "used before declaration" error.
                    const sourceDaySchedule = sourceScheduleByDay[i];

                    for (const staffId in sourceDaySchedule) {
                        if (!newSchedule[staffId]) {
                            newSchedule[staffId] = {};
                        }
                        // FIX: Logic bug where an incorrect variable was being accessed.
                        newSchedule[staffId][targetDateString] = { ...sourceDaySchedule[staffId] };
                    }
                }
                const nextDate = new Date(currentWeekStart);
                nextDate.setDate(nextDate.getDate() + 7);
                currentWeekStart = nextDate;
            }
            return newSchedule;
        });

        toast.success('Schedule repeated successfully for the selected period!');
    }, [schedule, schedulablePeople, weekStartDay]);
    
    const handleMarkNotificationAsRead = (notificationId: string) => {
        if (!isFirebaseInitialized) return;
        const currentUserId = user?.uid || loggedInStaff?.id;
        if (currentUserId) {
            markNotificationAsRead(currentUserId, notificationId);
        }
    };

    const handleMarkAllNotificationsAsRead = () => {
        if (!isFirebaseInitialized) return;
        const currentUserId = user?.uid || loggedInStaff?.id;
        if (currentUserId) {
            markAllNotificationsAsRead(currentUserId, notifications);
        }
    };

    const handleClearNotifications = () => {
        if (!isFirebaseInitialized) return;
        const currentUserId = user?.uid || loggedInStaff?.id;
        if (currentUserId) {
            clearNotifications(currentUserId);
        }
    };

    const handleScheduleGenerated = async (weekStartDate: Date) => {
        if (!isFirebaseInitialized) return;
        const title = "New Schedule Is Live! 🗓️";
        const message = `Fresh off the press! The new schedule for the week of ${formatDateRange(weekStartDate)} is published and ready for you to view.`;
        
        const allUserIds = [
            ...staffList.map(s => s.id),
            ...adminUsers.map(a => a.uid),
        ];
        
        const uniqueUserIds = [...new Set(allUserIds)];

        const promises = uniqueUserIds.map(userId => 
            createNotification(userId, { title, message, level: 'success' })
        );
        
        try {
            await Promise.all(promises);
            toast.success("Notifications sent to all users.");
        } catch (error) {
            console.error("Failed to send notifications:", error);
            toast.error("Could not send notifications.");
        }
    };

    const handleCreateTimeOffRequest = useCallback(async (requestData: Omit<TimeOffRequest, 'id' | 'status' | 'createdAt' | 'staffName'>) => {
        if (!isFirebaseInitialized) return;
        const staffMember = staffList.find(s => s.id === requestData.employeeId);
        if (!staffMember) {
            toast.error("Could not find the staff profile to submit the request.");
            return;
        }
        
        // --- START VALIDATION ---
        if (new Date(requestData.endDate) < new Date(requestData.startDate)) {
            toast.error("Validation failed: End date cannot be before the start date.");
            return;
        }

        if (requestData.type === 'REPLACED') {
            const { replacementEmployeeId, employeeId, startDate, endDate } = requestData;

            if (!replacementEmployeeId) {
                toast.error("Validation failed: A replacement must be selected for this request type.");
                return;
            }

            const replacementStaff = staffList.find(s => s.id === replacementEmployeeId);
            if (!replacementStaff) {
                toast.error("Validation failed: Could not find the selected replacement staff member.");
                return;
            }

            if (replacementEmployeeId === employeeId) {
                toast.error("Validation failed: You cannot select yourself as a replacement.");
                return;
            }

            const getResolvedAvailability = (person: StaffMember, date: Date): AvailabilityStatus => {
                const dateString = formatDateYYYYMMDD(date);
                if (person.availabilityOverrides && person.availabilityOverrides[dateString]) {
                    return person.availabilityOverrides[dateString];
                }
                const dayOfWeek = getDayOfWeekFromDate(date);
                return (person.availability && person.availability[dayOfWeek]) || 'Available';
            };
            
            const parseDateAsLocal = (dateString: string) => {
                const [year, month, day] = dateString.split('-').map(Number);
                return new Date(year, month - 1, day);
            };

            let currentDate = parseDateAsLocal(startDate);
            const loopEndDate = parseDateAsLocal(endDate);

            while (currentDate <= loopEndDate) {
                const dateString = formatDateYYYYMMDD(currentDate);

                const availability = getResolvedAvailability(replacementStaff, currentDate);
                if (availability === 'Unavailable') {
                    toast.error(`Validation failed: ${replacementStaff.name} is unavailable on ${dateString}.`);
                    return;
                }

                const replacementSchedule = schedule[replacementStaff.id];
                const entry = replacementSchedule?.[dateString];
                const conflictingStatuses: AssignmentStatus[] = ['ASSIGNED', 'SICK', 'VACATION', 'ON_CALL'];
                if (entry && conflictingStatuses.includes(entry.status)) {
                    toast.error(`Validation failed: ${replacementStaff.name} already has an assignment on ${dateString}.`);
                    return;
                }
                currentDate.setDate(currentDate.getDate() + 1);
            }
        }
        // --- END VALIDATION ---
        
        try {
            const timeOffRef = db.ref('timeOffRequests');
            const newRequestRef = timeOffRef.push();
            
            const newStatus = requestData.type === 'REPLACED' && requestData.replacementEmployeeId ? 'PENDING_REPLACER' : 'PENDING_ADMIN';

            const newRequestData: Omit<TimeOffRequest, 'id'> = {
                ...requestData,
                staffName: staffMember.name,
                status: newStatus,
                createdAt: new Date().toISOString(),
            };

            await newRequestRef.set(newRequestData);

            // Notify Admins for ALL requests
            let adminTitle = "New Time Off Request";
            let adminMessage = `${staffMember.name} is requesting leave from ${requestData.startDate} to ${requestData.endDate}. Your approval is needed.`;
            if (requestData.type === 'REPLACED' && requestData.replacementEmployeeId) {
                const replacementStaff = staffList.find(s => s.id === requestData.replacementEmployeeId);
                adminTitle = "New Cover Request";
                adminMessage = `${staffMember.name} has requested time off and nominated ${replacementStaff?.name || 'a colleague'} to cover. Please review.`;
            }
            const adminNotificationPromises = adminUsers.map(admin =>
                createNotification(admin.uid, { title: adminTitle, message: adminMessage, level: 'info' })
            );
            
            // Notify Replacement Staff if applicable
            const replacementNotificationPromise = () => {
                if (newStatus === 'PENDING_REPLACER' && requestData.replacementEmployeeId) {
                    const title = "You've Been Asked to Cover a Shift!";
                    const message = `Teamwork! ${staffMember.name} has asked if you can cover their shifts from ${requestData.startDate} to ${requestData.endDate}. Please respond in your portal.`;
                    return createNotification(requestData.replacementEmployeeId, { title, message, level: 'info' });
                }
                return Promise.resolve();
            };

            // Execute all promises
            await Promise.all([...adminNotificationPromises, replacementNotificationPromise()]);

        } catch (error: any) {
            console.error("Failed to create time off request:", error);
            toast.error("Could not submit the time off request. Please try again.");
            if (error.code === 'PERMISSION_DENIED') {
                setFirebasePermissionError(true);
            }
        }
    }, [staffList, adminUsers, schedule]);
    
    const handleReplacementDecision = useCallback(async (requestId: string, accepted: boolean) => {
        if (!isFirebaseInitialized) return;
        const request = timeOffRequests.find(r => r.id === requestId);
        if (!request || request.status !== 'PENDING_REPLACER') {
            toast.error("This request is no longer awaiting your response.");
            return;
        }

        const replacer = staffList.find(s => s.id === request.replacementEmployeeId);
        if (!replacer) return;

        try {
            const newStatus = accepted ? 'PENDING_ADMIN' : 'REJECTED';
            await db.ref(`timeOffRequests/${requestId}/status`).set(newStatus);
                
            if (accepted) {
                const title = "Cover Request Accepted - Ready for Approval";
                const message = `${replacer.name} has agreed to cover for ${request.staffName}. The time off request is now awaiting your final decision.`;
                const notificationPromises = adminUsers.map(admin =>
                    createNotification(admin.uid, { title, message, level: 'info' })
                );
                await Promise.all(notificationPromises);
                toast.success("You've accepted the cover request. It has been sent for admin approval.");
            } else {
                const title = "Update on Your Time Off Request";
                const message = `Unfortunately, ${replacer.name} was unable to cover your shift from ${request.startDate} to ${request.endDate}. You may need to cancel this request or find an alternative.`;
                await createNotification(request.employeeId, { title, message, level: 'warning' });
                toast.success("You have rejected the cover request.");
            }
        } catch (error) {
            console.error("Failed to process replacement decision:", error);
            toast.error("Could not process your decision. Please try again.");
        }
    }, [timeOffRequests, staffList, adminUsers]);


    const handleUpdateTimeOffRequestStatus = useCallback(async (requestId: string, status: 'APPROVED' | 'REJECTED', scheduleStatus: AssignmentStatus = 'VACATION') => {
        if (!isFirebaseInitialized) return;
        const request = timeOffRequests.find(r => r.id === requestId);
        if (!request) {
            toast.error("Could not find the request to update.");
            return;
        }
        
        if (request.status !== 'PENDING_ADMIN') {
            toast.error("This request is not awaiting admin approval.");
            return;
        }

        const updatePromise = async () => {
            await db.ref(`timeOffRequests/${requestId}/status`).set(status);
    
            if (status === 'APPROVED') {
                const scheduleUpdates: { [key: string]: any } = {};
                let currentDate = new Date(request.startDate);
                const endDate = new Date(request.endDate);

                while (currentDate <= endDate) {
                    const dateString = formatDateYYYYMMDD(currentDate);

                    const requesterPath = `/scheduleData/schedule/${request.employeeId}/${dateString}`;
                    scheduleUpdates[requesterPath] = { staffId: request.employeeId, shiftId: null, status: scheduleStatus };

                    if (request.type === 'REPLACED' && request.replacementEmployeeId) {
                        const originalEntry = schedule[request.employeeId]?.[dateString];
                        if (originalEntry && originalEntry.status === 'ASSIGNED' && originalEntry.shiftId) {
                            const replacementPath = `/scheduleData/schedule/${request.replacementEmployeeId}/${dateString}`;
                            scheduleUpdates[replacementPath] = { staffId: request.replacementEmployeeId, shiftId: originalEntry.shiftId, status: 'ASSIGNED' };
                        }
                    }
                    currentDate.setDate(currentDate.getDate() + 1);
                }
                await db.ref().update(scheduleUpdates);
            }
            
            // Notification for original requester
            let title: string;
            let message: string;
            let level: 'success' | 'warning' = status === 'APPROVED' ? 'success' : 'warning';

            if (status === 'APPROVED') {
                title = "Time Off Approved! 🎉";
                message = `Great news! Your time off request for ${request.startDate} to ${request.endDate} has been approved. Enjoy your time off!`;
            } else { // REJECTED
                title = "Time Off Request Update";
                message = `Your time off request for ${request.startDate} to ${request.endDate} has been declined. Please see an administrator for more details.`;
            }
            await createNotification(request.employeeId, { title, message, level });
            
            // Notification for replacement staff
            if (status === 'APPROVED' && request.type === 'REPLACED' && request.replacementEmployeeId) {
                 const replacerTitle = "Cover Shift Confirmed ✅";
                 const replacerMessage = `The cover arrangement for ${request.staffName} (${request.startDate} - ${request.endDate}) is approved and has been added to your schedule. Thanks for your help!`;
                 await createNotification(request.replacementEmployeeId, { title: replacerTitle, message: replacerMessage, level: 'success' });
            }

            return `Request has been ${status.toLowerCase()}.`;
        };

        toast.promise(updatePromise(), {
            loading: 'Updating request status...',
            success: (message) => message,
            error: (err: any) => {
                console.error("Failed to update time off request:", err);
                return (err as Error).message || "Failed to update request status.";
            }
        });
    }, [timeOffRequests, schedule]);

    const handleDeleteTimeOffRequest = useCallback(async (requestId: string) => {
        if (!isFirebaseInitialized) return;
        const requestToDelete = timeOffRequests.find(r => r.id === requestId);
        if (!requestToDelete) {
            throw new Error("Request not found.");
        }

        const isAdmin = !!user;
        const isOwner = loggedInStaff?.id === requestToDelete.employeeId;
        const isPending = requestToDelete.status === 'PENDING_ADMIN' || requestToDelete.status === 'PENDING_REPLACER';

        if (isAdmin || (isOwner && isPending)) {
            try {
                await db.ref(`timeOffRequests/${requestId}`).remove();
            } catch(err) {
                if ((err as any).code === 'PERMISSION_DENIED') {
                    setFirebasePermissionError(true);
                }
                throw new Error("Failed to delete request.");
            }
        } else {
            throw new Error("You don't have permission to delete this request.");
        }
    }, [user, loggedInStaff, timeOffRequests]);

    const handleSaveParkingReservation = useCallback((reservation: Omit<ParkingReservation, 'id'> & { id?: string }) => {
        setParkingReservations(prev => {
            if (reservation.id) {
                return prev.map(r => r.id === reservation.id ? { ...reservation, id: reservation.id } as ParkingReservation : r);
            }
            return [...prev, { ...reservation, id: Date.now().toString() } as ParkingReservation];
        });
        toast.success(reservation.id ? 'Parking reservation updated' : 'Parking reservation created');
    }, []);

    const handleDeleteParkingReservation = useCallback((id: string) => {
        setParkingReservations(prev => prev.filter(r => r.id !== id));
        toast.success('Parking reservation deleted');
    }, []);

    const handleInitiatePrint = (data: PrintData) => {
        setPrintData(data);
    };


    if (isLoading) return <LoadingSpinner />;
    
    if (printData) {
        return <PrintPage {...printData} onDone={() => setPrintData(null)} />;
    }

    if (pdfPreviewData) {
        return <PDFPreviewPage {...pdfPreviewData} onDone={() => setPdfPreviewData(null)} />;
    }
    
    if (shuttlePrintData) {
        return <ShuttlePrintPage {...shuttlePrintData} onDone={() => setShuttlePrintData(null)} />;
    }

    if (parkingPrintData) {
        return <ParkingPrintPage {...parkingPrintData} onDone={() => setParkingPrintData(null)} />;
    }

    const isLoggedIn = !!(user || loggedInStaff);
    const isAdmin = !!user;

    const renderContent = () => {
        if (user && !allowedPages.includes(view.page)) {
             return (
                <div className="flex flex-col items-center justify-center p-12 text-center bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-200 dark:border-slate-700">
                    <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Icons.ShieldAlert className="w-8 h-8 text-amber-600 dark:text-amber-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Access Restricted</h2>
                    <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                        You do not have permission to access the <span className="font-bold capitalize">{view.page}</span> page. Please contact a super administrator if you believe this is an error.
                    </p>
                    <button 
                        onClick={() => setView({ page: allowedPages[0] || 'scheduler' })}
                        className="mt-8 px-6 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-all shadow-sm"
                    >
                        Go to {allowedPages[0] || 'Scheduler'}
                    </button>
                </div>
             );
        }
        if (firebaseInitError) {
            return (
                <div className="flex flex-col items-center justify-center min-h-screen p-4 text-center bg-gray-50 dark:bg-slate-900">
                    <div className="p-8 bg-white rounded-2xl shadow-xl dark:bg-slate-800 max-w-md border border-red-100 dark:border-red-900/30">
                        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Configuration Error</h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            {firebaseInitError}
                        </p>
                        <div className="text-sm text-gray-500 dark:text-gray-500 bg-gray-50 dark:bg-slate-900/50 p-4 rounded-lg border border-gray-100 dark:border-slate-700 text-left">
                            <p className="font-semibold mb-2">How to fix this:</p>
                            <ol className="list-decimal ml-4 space-y-1">
                                <li>Ensure your <code className="bg-gray-200 dark:bg-slate-700 px-1 rounded">.env</code> file exists.</li>
                                <li>Check that all Firebase variables are correctly set.</li>
                                <li>Restart the development server if you just added the file.</li>
                            </ol>
                        </div>
                        <button 
                            onClick={() => window.location.reload()}
                            className="mt-8 w-full py-3 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-indigo-500/30"
                        >
                            Retry Initialization
                        </button>
                    </div>
                </div>
            );
        }

        if (firebasePermissionError) return <PermissionErrorMessage />;

        if (!isLoggedIn) return <Login onLogin={handleLogin} onForgotPassword={handleForgotPassword} onRegisterFirstAdmin={handleRegisterFirstAdmin} adminCount={adminUsers.length} propertyName={propertyName} />;
        if (loggedInStaff) {
             return <StaffPortal
                staffMember={loggedInStaff}
                shifts={shifts}
                schedule={schedule}
                weekStartDay={weekStartDay}
                departments={departments}
                allStaff={staffList.filter(s => s.status !== 'deleted')}
                onSaveStaff={handleStaffUpdateBySelf}
                notifications={notifications}
                onMarkNotificationAsRead={handleMarkNotificationAsRead}
                onMarkAllNotificationsAsRead={handleMarkAllNotificationsAsRead}
                onClearNotifications={handleClearNotifications}
                timeOffRequests={timeOffRequests} // Pass all requests for cover functionality
                onCreateTimeOffRequest={handleCreateTimeOffRequest}
                onDeleteTimeOffRequest={handleDeleteTimeOffRequest}
                onReplacementDecision={handleReplacementDecision}
                shuttleReservations={shuttleReservations}
                pickupLocations={pickupLocations}
                onLogout={handleLogout}
                onSaveShuttleReservation={handleSaveShuttleReservation}
                onDeleteShuttleReservation={handleDeleteShuttleReservation}
                onInitiateShuttlePrint={(date) => setShuttlePrintData({ pickupLocations, reservations: shuttleReservations.filter(r => r.date === date), selectedDate: date, propertyName })}
                propertyName={propertyName}
            />;
        }
        if (isAdmin && user) {
            switch (view.page) {
                case 'scheduler': return <SchedulerView 
                    departments={departments} 
                    people={schedulablePeople} 
                    shifts={shifts} 
                    schedule={schedule} 
                    weeklyMetrics={weeklyMetrics}
                    setWeeklyMetrics={setWeeklyMetrics}
                    weekStartDay={weekStartDay} 
                    setSchedule={setSchedule} 
                    setView={setView} 
                    onScheduleGenerated={handleScheduleGenerated} 
                    onCellUpdate={handleScheduleUpdateByAdmin} 
                    onRepeatSchedule={handleRepeatSchedule} 
                    onInitiatePrint={handleInitiatePrint} 
                    onInitiatePDFPreview={(data) => setPdfPreviewData(data)} 
                />;
                case 'staffList': return <StaffManagement staffList={staffList} departments={departments} weekStartDay={weekStartDay} setView={setView} onSaveStaff={handleStaffProfileUpdateByAdmin} onDeleteStaff={handleDeleteStaff} />;
                case 'timeOff': return <TimeOffManagementView requests={timeOffRequests} staffList={staffList} onUpdateRequestStatus={handleUpdateTimeOffRequestStatus} onDeleteRequest={handleDeleteTimeOffRequest} onCreateRequest={handleCreateTimeOffRequest} />;
                case 'settings': return <SettingsView 
                    departments={departments} 
                    shifts={shifts} 
                    pickupLocations={pickupLocations} 
                    reservationSources={reservationSources}
                    weekStartDay={weekStartDay}
                    propertyName={propertyName}
                    onSaveDepartment={handleSaveDepartment} 
                    onDeleteDepartment={handleDeleteDepartment} 
                    onSaveShift={handleSaveShift} 
                    onDeleteShift={handleDeleteShift} 
                    onSavePickupLocation={handleSavePickupLocation} 
                    onDeletePickupLocation={handleDeletePickupLocation} 
                    onSaveReservationSource={handleSaveReservationSource}
                    onDeleteReservationSource={handleDeleteReservationSource}
                    onUpdateWeekStartDay={setWeekStartDay}
                    onUpdatePropertyName={setPropertyName}
                    currentUser={user}
                    adminUsers={adminUsers}
                    onCreateAdmin={handleCreateAdmin}
                    onDeleteCurrentUser={handleDeleteCurrentUser}
                    onUpdateAdminStatus={handleUpdateAdminStatus}
                    onDeleteAdmin={handleDeleteAdmin}
                    onSaveAdmin={handleSaveAdmin}
                />;
                case 'shuttle': return <ShuttleReservationView pickupLocations={pickupLocations} reservations={shuttleReservations.filter(r => r.date === selectedDate)} onSaveReservation={(res) => handleSaveShuttleReservation({ ...res, date: selectedDate })} onDeleteReservation={handleDeleteShuttleReservation} selectedDate={selectedDate} onDateChange={setSelectedDate} onInitiatePrint={() => setShuttlePrintData({ pickupLocations, reservations: shuttleReservations.filter(r => r.date === selectedDate), selectedDate, propertyName })} />;
                case 'parking': return <ParkingReservationView reservations={parkingReservations} reservationSources={reservationSources} onSaveReservation={handleSaveParkingReservation} onDeleteReservation={handleDeleteParkingReservation} onInitiatePrint={(reservation) => setParkingPrintData({ reservation, propertyName })} />;
                case 'workforce': return <WorkforceTracker people={schedulablePeople} shifts={shifts} schedule={schedule} />;
                case 'reports': return <ReportsView />;
                case 'myProfile': return currentAdminUser ? <AdminProfileView adminUser={currentAdminUser} departments={departments} onSave={handleSaveAdmin} /> : <LoadingSpinner />;
                case 'help': return <HelpCenterView />;
                default: return <div>Page not found</div>;
            }
        }
        return <Login onLogin={handleLogin} onForgotPassword={handleForgotPassword} onRegisterFirstAdmin={handleRegisterFirstAdmin} adminCount={adminUsers.length} />;
    };
    
    return (
        <div className="min-h-screen bg-gray-50 text-gray-800 dark:bg-slate-900 dark:text-gray-200">
            <Toaster position="top-center" reverseOrder={false} toastOptions={{
                 style: isDarkMode ? {
                    border: '1px solid #4A5568',
                    padding: '16px',
                    color: '#F7FAFC',
                    backgroundColor: '#2D3748',
                } : {
                    border: '1px solid #E2E8F0',
                    padding: '16px',
                    color: '#1A202C',
                    backgroundColor: '#FFFFFF',
                },
                 success: {
                    iconTheme: {
                        primary: isDarkMode ? '#48BB78' : '#34D399',
                        secondary: '#F7FAFC',
                    },
                },
                error: {
                    iconTheme: {
                        primary: isDarkMode ? '#F56565' : '#EF4444',
                        secondary: '#F7FAFC',
                    },
                },
            }} />
            {isLoggedIn && !loggedInStaff && (
                <Header
                    isLoggedIn={isLoggedIn}
                    isAdmin={isAdmin}
                    userName={currentAdminUser?.name || user?.email || loggedInStaff?.name || ''}
                    onLogout={handleLogout}
                    view={view}
                    setView={handleSetView}
                    isSaving={isSaving}
                    notifications={notifications}
                    onMarkNotificationAsRead={handleMarkNotificationAsRead}
                    onMarkAllNotificationsAsRead={handleMarkAllNotificationsAsRead}
                    onClearNotifications={handleClearNotifications}
                    theme={theme}
                    setTheme={setTheme}
                    propertyName={propertyName}
                    allowedPages={allowedPages}
                />
            )}
            <main className={loggedInStaff ? "" : "p-4 sm:p-6 lg:p-8"}>{renderContent()}</main>
        </div>
    );
};

export default App;